import { PageHeading } from '@portal_components/components/PageHeading';
import { Checkbox } from '@portal_components/components/Checkbox';
import { Button } from '@portal_components/components/Button';
import { useDefinedParams } from '../hooks/useDefinedParams';
import { usePortalQuery, EHttpMethod } from '../hooks/usePortalQuery';
import { useState } from 'react';
import axios from 'axios';
import { EnvironmentVariables } from '../EnvironmentVariables';
import { BaseExceptionDetails, EExceptionType, EMpanAssignmentMode, HandoverExceptions } from '../types';
import { IconButton } from '@portal_components/components/IconButton';
import { MeterPointDetailsForm } from '../elements/MeterPointDetailsForm/MeterPointDetailsForm';
import { useUserInfo } from '../hooks/useUserInfo';
import { EUserRole } from '@portal_components/types';

export const NoAppointmentExceptionsPage = () => {
  const { userInfo } = useUserInfo();
  const [handoverFileName] = useDefinedParams(['handoverFileName']);
  const [submitResponse, setSubmitResponse] = useState<{ success: boolean; message: string } | null>(null);
  const [acknowledgedExceptions, setAcknowledgedExceptions] = useState<Record<string, boolean>>({});
  const [resolvedExceptions, setResolvedExceptions] = useState<Record<string, boolean>>({});
  const [expandedMpan, setExpandedMpan] = useState<string | null>(null);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  const { data: noAppointmentExceptions, isLoading: exceptionsLoading } = usePortalQuery<HandoverExceptions>(
    EHttpMethod.GET,
    `exceptions?exceptionType=${EExceptionType.NO_APPOINTMENT_EXCEPTION}&handoverFileName=${encodeURIComponent(handoverFileName)}`,
    `get-no-appointment-exceptions-${handoverFileName}`,
    true
  );

  const resolveAcknoledgedExceptions = async () => {
    if (formSubmitted) return;
    setFormSubmitted(true);
    const mpans = Object.entries(acknowledgedExceptions)
      .filter(([, acknowledged]) => !!acknowledged)
      .map(([mpan]) => mpan);
    if (!mpans || mpans.length === 0) return;
    const response = await axios
      .delete(`${EnvironmentVariables.ApiUrl}/exceptions?handoverFileName=${encodeURIComponent(handoverFileName)}&exceptionType=${EExceptionType.NO_APPOINTMENT_EXCEPTION}`, {
        data: { mpans },
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      })
      .catch(error => error.response);
    if (!response || response.status !== 200) {
      setSubmitResponse({ success: false, message: response.data ?? 'Failed to resolve exceptions' });
      setFormSubmitted(false);
      return;
    }

    setSubmitResponse({ success: true, message: 'Exception cleared, the MPAN remains unappointed' });
    setAcknowledgedExceptions({});
    setResolvedExceptions({ ...resolvedExceptions, ...acknowledgedExceptions });
    setTimeout(() => {
      setFormSubmitted(false);
      setSubmitResponse(null);
    }, 5000);
  };

  const renderException = ({ siteDetails }: BaseExceptionDetails) => {
    const mpan = siteDetails?.mprn;
    if (!mpan) return null;
    const acknowledged = !!acknowledgedExceptions[mpan];
    const resolved = !!resolvedExceptions[mpan];
    const borderColour = resolved ? 'border-feedback-green' : 'border-interactive-coral';
    return (
      <div key={mpan}>
        <div className={`mb-6 rounded-l-sm border-l-[4px] ${borderColour} bg-elevation-1 py-3 px-4 flex flex-row justify-between items-center`}>
          <p className="font-bold">{mpan}</p>
          <div className="flex flex-row gap-8 h-full items-center">
            {resolved && <p className="text-feedback-green">Exception Resolved</p>}
            <div className="flex flex-row justify-center items-center">
              {expandedMpan === mpan ? <IconButton icon="/arrowUp.svg" onClick={() => setExpandedMpan(null)} /> : <IconButton icon="/arrowDown.svg" onClick={() => setExpandedMpan(mpan)} />}
            </div>
            {userInfo && userInfo.role !== EUserRole.READONLY_USER && (
              <Checkbox title="Acknowledge" checked={acknowledged} readonly={resolved} onSelected={checked => setAcknowledgedExceptions({ ...acknowledgedExceptions, [mpan]: checked })} />
            )}
          </div>
        </div>
        {expandedMpan === mpan && (
          <MeterPointDetailsForm
            mode={EMpanAssignmentMode.VIEW}
            existingMeterPointDetails={siteDetails}
            customerGroupId={noAppointmentExceptions?.customerGroupId}
            contractId={noAppointmentExceptions?.contractId}
            mpan={mpan}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <PageHeading
        title={`Unappointed Exceptions for ${handoverFileName}`}
        navigationButtons={[
          {
            title: 'Back',
            path: '/exceptions',
            icon: '/back.svg',
            className: 'w-6 h-6'
          }
        ]}
      />
      {exceptionsLoading && <div>Loading exceptions...</div>}
      {!exceptionsLoading && noAppointmentExceptions && (
        <>
          <div className="flex flex-row justify-between">
            <div className="pb-4">
              <p>Customer Group ID: {noAppointmentExceptions.customerGroupId}</p>
              <p>Contract ID: {noAppointmentExceptions.contractId}</p>
            </div>
            {userInfo && userInfo.role !== EUserRole.READONLY_USER && (
              <div className="flex flex-col items-end text-end">
                <Button primary text="Resolve Exceptions" onClick={resolveAcknoledgedExceptions} disabled={!Object.values(acknowledgedExceptions).find(exception => !!exception) || formSubmitted} />
                {submitResponse ? <p className={`py-2 ${submitResponse.success ? 'text-feedback-green' : 'text-feedback-red'}`}>{submitResponse.message}</p> : <div className="h-10" />}
              </div>
            )}
          </div>
          {noAppointmentExceptions.exceptions.length > 0 ? noAppointmentExceptions.exceptions.map(renderException) : <p>No exceptions were found for {handoverFileName}</p>}
        </>
      )}
    </div>
  );
};
